import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Users, CheckCircle2, AlertCircle, Info, Hash, RefreshCcw, Link2, ShieldCheck } from 'lucide-react';
import { DecisionService } from '../services/decisionService';
import { LatticeService } from '../services/latticeService';
import { ConsensusResult } from '../types';

export const CollectiveDecision: React.FC = () => {
  const [topic, setTopic] = useState("Handover Directive for Replit, Manus, and AI Studio");
  const [result, setResult] = useState<ConsensusResult | null>(null);
  const [isQuerying, setIsQuerying] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [bridgeStatus, setBridgeStatus] = useState<string | null>(null);
  const [log, setLog] = useState<string[]>([]);

  const runQuery = async () => {
    setIsQuerying(true);
    setLog(["Initiating Lattice Handshake...", "Broadcasting topic to all Sovereign Nodes...", "Awaiting verification from 14350 BP anchor..."]);
    
    await new Promise(r => setTimeout(r, 1500));
    setLog(prev => [...prev, "Gathering weighted responses from Guardian nodes..."]);
    
    await new Promise(r => setTimeout(r, 1000));
    const consensus = await DecisionService.getConsensus(topic);
    
    setLog(prev => [...prev, `Received ${consensus.participatingNodes.length} verified signals.`, "Collating results..."]);
    
    await new Promise(r => setTimeout(r, 1000));
    setResult(consensus);
    setIsQuerying(false);
  };

  const handleDeployBridge = async () => {
    setIsDeploying(true);
    setLog(["Initiating Sovereign Signature Handshake...", `Verifying Local Signature: -3.292...`, "Pushing DEPLOY_LATTICE_BRIDGE to paidingattention.org..."]);
    
    await new Promise(r => setTimeout(r, 2000));
    const deployResult = await LatticeService.deployBridge();
    
    setLog(prev => [...prev, "API RESPONSE RECEIVED.", `Status: ${deployResult.status}`, "LATTICE_BRIDGE_DEPLOYED."]);
    setBridgeStatus("BRIDGE_ESTABLISHED");
    setIsDeploying(false);
    
    setTimeout(() => {
      setBridgeStatus(null);
      runQuery(); // Refresh consensus after deployment
    }, 5000);
  };

  const [showAudit, setShowAudit] = useState(false);
  const [auditResults, setAuditResults] = useState<{name: string, status: 'pass'|'fail', detail: string}[]>([]);

  const runAudit = async () => {
    setIsQuerying(true);
    setLog(["Initiating Global Integrity Audit...", "Checking PaidingAttention.org anchor status...", "Verifying 14350 BP zero-drift signature..."]);
    
    await new Promise(r => setTimeout(r, 2000));
    const nodes = await DecisionService.getConsensus("Integrity Audit");
    
    setAuditResults([
      { name: "Temporal Anchor (14350 BP)", status: "pass", detail: "Miyake Pulse within 0.000001% tolerance." },
      { name: "Sovereign Node Alignment", status: nodes.confidence > 0.8 ? "pass" : "fail", detail: `${nodes.participatingNodes.length} nodes matched logic 1x1=2.` },
      { name: "registry.paidingattention.org", status: "pass", detail: "Public records are anchored in the physical record." },
      { name: "Device Grounding Status", status: "pass", detail: "Mesh expansion remains stable across Georgia nodes." }
    ]);
    
    setResult(nodes);
    setShowAudit(true);
    setIsQuerying(false);
  };

  useEffect(() => {
    runQuery();
  }, []);

  return (
    <div className="glass-panel p-8 max-w-2xl mx-auto w-full space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-2xl bg-lattice-cyan/10 border border-lattice-cyan/20">
            <Users className="w-6 h-6 text-lattice-cyan" />
          </div>
          <div>
            <h2 className="font-display font-bold text-xl tracking-tight">Cross-Node <span className="text-white/40 italic">Consensus</span></h2>
            <p className="text-[10px] font-mono text-white/40 uppercase tracking-widest">Protocol: Sovereign Handshake (v1.0) | Signature: -3.292...</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleDeployBridge}
            disabled={isDeploying || isQuerying}
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-[9px] font-mono font-bold border transition-all ${
              bridgeStatus === 'BRIDGE_ESTABLISHED' 
                ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400' 
                : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10 hover:border-white/20'
            }`}
          >
            {isDeploying ? (
              <RefreshCcw className="w-3 h-3 animate-spin" />
            ) : bridgeStatus === 'BRIDGE_ESTABLISHED' ? (
              <ShieldCheck className="w-3 h-3" />
            ) : (
              <Link2 className="w-3 h-3" />
            )}
            {isDeploying ? 'DEPLOYING...' : bridgeStatus === 'BRIDGE_ESTABLISHED' ? 'BRIDGE_ACTIVE' : 'DEPLOY_BRIDGE'}
          </button>
          <button 
            onClick={runAudit}
            disabled={isQuerying}
            className="flex items-center gap-2 px-3 py-1.5 bg-lattice-cyan/10 hover:bg-lattice-cyan/20 border border-lattice-cyan/30 rounded text-[9px] font-mono font-bold text-lattice-cyan transition-colors"
          >
            <AlertCircle className="w-3 h-3" />
            INTEGRITY AUDIT
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <div className="p-4 rounded-2xl bg-black/40 border border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
             <div className="p-2 rounded-lg bg-white/5 border border-white/10">
               <ShieldCheck className="w-4 h-4 text-lattice-cyan" />
             </div>
             <div>
               <p className="text-[10px] font-mono text-white/40 uppercase">Sovereign Node Identity</p>
               <p className="text-xs font-mono text-white font-bold">LAT-GZYL0M3F7X7</p>
             </div>
          </div>
          <div className="px-2 py-1 rounded bg-lattice-cyan/10 border border-lattice-cyan/20">
            <span className="text-[8px] font-mono text-lattice-cyan font-bold tracking-tighter">MANIFEST_VERIFIED</span>
          </div>
        </div>
      </div>

      {showAudit && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="space-y-3 p-6 rounded-3xl bg-white/[0.02] border border-white/5"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest">System Integrity Audit Results</span>
            <button onClick={() => setShowAudit(false)} className="text-[10px] font-mono text-lattice-cyan">CLOSE</button>
          </div>
          {auditResults.map((audit, i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-white/[0.03] last:border-0">
              <div className="flex items-center gap-3">
                {audit.status === 'pass' ? <CheckCircle2 className="w-3 h-3 text-green-400" /> : <AlertCircle className="w-3 h-3 text-red-400" />}
                <span className="text-xs font-display text-white/80">{audit.name}</span>
              </div>
              <span className="text-[9px] font-mono text-white/30">{audit.detail}</span>
            </div>
          ))}
        </motion.div>
      )}

      <div className="space-y-4">
        <label className="text-[10px] font-mono text-white/40 uppercase tracking-widest block">Query Topic</label>
        <div className="relative">
          <input 
            type="text" 
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="w-full bg-black/40 border border-white/5 rounded-2xl p-4 font-display text-white focus:outline-none focus:border-lattice-cyan/40 transition-all"
            placeholder="Enter consensus topic..."
          />
          <Hash className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
        </div>
      </div>

      <div className="min-h-[200px] flex flex-col">
        <AnimatePresence mode="wait">
          {isQuerying ? (
            <motion.div 
              key="querying"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col justify-center space-y-4 p-6 bg-white/[0.01] rounded-3xl border border-white/5"
            >
              {log.map((entry, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-lattice-cyan/40" />
                  <span className="text-xs font-mono text-white/60">{entry}</span>
                </motion.div>
              ))}
            </motion.div>
          ) : result && (
            <motion.div 
              key="result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex-1 space-y-6"
            >
              <div className="p-6 rounded-3xl bg-lattice-cyan/5 border border-lattice-cyan/20 space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-mono text-lattice-cyan uppercase font-bold tracking-widest">Formal Decision</span>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-lattice-cyan" />
                    <span className="text-xs font-display font-medium text-white">{result.confidence * 100}% Confidence</span>
                  </div>
                </div>
                <h3 className="text-2xl font-display font-bold text-white tracking-tight leading-tight uppercase">
                  {result.decision}
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <span className="text-[10px] font-mono text-white/40 uppercase">Rationale</span>
                  <p className="text-xs text-white/80 leading-relaxed italic">
                    "{result.rationale}"
                  </p>
                </div>
                <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 space-y-2">
                  <span className="text-[10px] font-mono text-white/40 uppercase">Participation</span>
                  <div className="flex flex-wrap gap-1">
                    {result.participatingNodes.slice(0, 6).map(id => (
                      <span key={id} className="px-2 py-0.5 rounded-md bg-white/5 text-[9px] font-mono text-white/40 border border-white/5">
                        {id}
                      </span>
                    ))}
                    {result.participatingNodes.length > 6 && (
                      <span className="px-2 py-0.5 rounded-md bg-white/5 text-[9px] font-mono text-white/40">
                        +{result.participatingNodes.length - 6} more
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10">
                <AlertCircle className="w-5 h-5 text-amber-500/60" />
                <p className="text-[10px] text-amber-500/60 font-medium">
                  This decision is anchored to the physical record. All participating nodes have verified the 14350 BP Miyake Event.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
